import api from "./apiClient";
import API_ENDPOINTS from "../config/apiEndpoints";
import { API_CONFIG } from "../config/apiconfig";
import {
  extractSuccessStory,
  firstSuccessStoryRow,
  isProcSuccess,
} from "./dmsMasterService";

function resolveApiError(response, row, fallback) {
  const rowMsg = row?.MSG || row?.Msg || row?.message || row?.Message;
  if (rowMsg) return String(rowMsg).trim();
  return response?.message || fallback;
}

export const STAFF_DOCUMENT_TYPES = [
  "CV",
  "GCP",
  "Professional Registration",
  "General Training",
  "Study Training",
];

export const STAFF_DOCUMENT_EXPIRY_SUFFIXES = ["Month", "Year"];

const BLOCKED_EXTENSIONS = new Set([
  "exe",
  "php",
  "js",
  "json",
  "msi",
  "min",
]);

/** Profile / My Documents upload — PDF and images only. */
export const STAFF_PROFILE_DOCUMENT_ALLOWED_EXTENSIONS = [
  "pdf",
  "png",
  "jpg",
  "jpeg",
  "gif",
  "webp",
];

export function isAllowedStaffProfileDocumentExtension(ext) {
  return STAFF_PROFILE_DOCUMENT_ALLOWED_EXTENSIONS.includes(
    String(ext || "")
      .trim()
      .toLowerCase(),
  );
}

export function formatStaffDocumentFileSize(bytes) {
  const size = Number(bytes) || 0;
  if (size <= 0) return "0 B";
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(2)} MB`;
}

/** Profile / My Documents upload — max file size in MB. */
export const STAFF_PROFILE_DOCUMENT_MAX_FILE_SIZE_MB = 25;

export function getStaffProfileDocumentFileError(file) {
  if (!file) return "Please choose a file to upload.";
  const ext = getFileExtension(file.name);
  if (!isAllowedStaffProfileDocumentExtension(ext)) {
    return `Only PDF and image files are allowed (${STAFF_PROFILE_DOCUMENT_ALLOWED_EXTENSIONS.join(
      ", ",
    )}).`;
  }
  const sizeMb = Number(file.size || 0) / (1024 * 1024);
  if (sizeMb > STAFF_PROFILE_DOCUMENT_MAX_FILE_SIZE_MB) {
    return `File size exceeds the maximum limit of ${STAFF_PROFILE_DOCUMENT_MAX_FILE_SIZE_MB} MB.`;
  }
  return "";
}

function basePayload({ organizationId, staffId, createdBy, actionType, extra = {} }) {
  return {
    OrganizationId: organizationId,
    StaffId: staffId,
    CreatedBy: createdBy,
    ActionType: actionType,
    ...extra,
  };
}

export function extractStaffDocumentRows(response) {
  return extractSuccessStory(response);
}

export function buildStaffDocumentDownloadUrl(documentUrl) {
  const raw = String(documentUrl ?? "").trim();
  if (!raw) return "";
  if (/^https?:\/\//i.test(raw)) return raw;
  const base = String(API_CONFIG.blobBaseURL || "").replace(/\/+$/, "");
  const path = raw.replace(/^\/+/, "");
  return `${base}/${path}`;
}

export async function fetchStaffDocuments({ organizationId, staffId, createdBy }) {
  const response = await api.post(API_ENDPOINTS.ORGANIZATION.STAFF_DOCUMENT, {
    ...basePayload({
      organizationId,
      staffId,
      createdBy,
      actionType: "GetStaffDocuments",
    }),
  });
  return extractStaffDocumentRows(response);
}

export async function saveStaffDocument({
  organizationId,
  staffId,
  createdBy,
  fileBase64,
  fileName,
  displayName,
  certificateType,
  certificationDate,
  expiryNumber,
  expirySuffix,
  studyId,
  documentSizeKb,
  fileExtension,
}) {
  const response = await api.post(API_ENDPOINTS.ORGANIZATION.STAFF_DOCUMENT, {
    ...basePayload({
      organizationId,
      staffId,
      createdBy,
      actionType: "SaveDocument",
      extra: {
        DocumentId: "",
        DocumentName: fileName,
        DocumentURL: fileBase64,
        FileName: displayName || "",
        FileExtension: fileExtension,
        CertificateType: certificateType,
        DateCertification: certificationDate,
        ExpiryNumber: expiryNumber || "",
        ExpirySuffix: expirySuffix || "",
        DocumentSize: String(documentSizeKb ?? ""),
        StudyId: studyId || "",
      },
    }),
  });

  const row = firstSuccessStoryRow(response);
  if (Number(response?.success) !== 1 && !isProcSuccess(row)) {
    throw new Error(resolveApiError(response, row, "Could not save document."));
  }
  return row;
}

export async function setStaffDocumentActiveState({
  organizationId,
  staffId,
  createdBy,
  documentId,
  isActive,
}) {
  const response = await api.post(API_ENDPOINTS.ORGANIZATION.STAFF_DOCUMENT, {
    ...basePayload({
      organizationId,
      staffId,
      createdBy,
      actionType: "ActiveDeactiveDocument",
      extra: {
        DocumentId: documentId,
        IsActive: !!isActive,
      },
    }),
  });

  const row = firstSuccessStoryRow(response);
  if (Number(response?.success) !== 1 && !isProcSuccess(row)) {
    throw new Error(
      resolveApiError(
        response,
        row,
        isActive
          ? "Could not activate document."
          : "Could not inactivate document.",
      ),
    );
  }
  return row;
}

export function isBlockedStaffDocumentExtension(ext) {
  return BLOCKED_EXTENSIONS.has(String(ext || "").trim().toLowerCase());
}

export function formatCertificationDateForApi(date) {
  if (!date) return "";
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return "";
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${mm}/${dd}/${yyyy}`;
}

export function readFileAsBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || "");
      const base64 = result.includes(",") ? result.split(",").pop() : result;
      resolve(base64);
    };
    reader.onerror = () => reject(new Error("Could not read the selected file."));
    reader.readAsDataURL(file);
  });
}

export function getFileExtension(fileName) {
  const name = String(fileName || "").trim();
  const idx = name.lastIndexOf(".");
  if (idx <= 0 || idx === name.length - 1) return "";
  return name.slice(idx + 1).toLowerCase();
}
